import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { FileItem, downloadFileFromUrl } from '@/common';
import { Report } from '../types';
import { getMessageFromError } from '@/core';
import { downloadCertificateFromDws } from '@/features/client-certificates/api/downloadFileFromCertificate';

type ReportFilesModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  reportId: string;
  reports?: Report[];
};

const ReportFilesModal = ({ show, setShow, reportId, reports }: ReportFilesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const downloadFileMutation = useMutation({
    mutationFn: downloadCertificateFromDws,
    onSuccess: async () => {
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const report =
    reports && reports.length > 0 ? reports.find((rep) => rep.id === reportId) : undefined;

  return (
    <Dialog fullScreen={fullScreen} open={show} onClose={() => setShow(false)} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">
          {t('Report - Files', { defaultValue: 'Report - Files' })}
        </Typography>
        <CloseIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={() => setShow(false)} />
      </DialogTitle>
      <DialogContent>
        <Box>
          {report && report.files.length > 0 ? (
            <List>
              {report.files.map((item) => (
                <FileItem
                  key={item.id}
                  name={item.title || ''}
                  uploadedBy={item.uploadedBy}
                  uploadedAt={item.uploadedAt}
                  fileName={item.fileName}
                  info={item.info}
                  onDownload={() => {
                    if (item.dwsDok)
                      downloadFileMutation.mutate({
                        dok: item.dwsDok,
                        filename: item.title || item.dwsDok.originalfilename,
                      });
                  }}
                />
              ))}
            </List>
          ) : (
            <Typography variant="subtitle1">
              {t('No files', { defaultValue: 'No files' })}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportFilesModal;
